// services/EnquiryService.js

import {
  createDocument,
  updateDocument,
  deleteDocument,
  getDocument,
  getCollection,
  getCollectionQuery,
} from "./FirebaseService";

import {
  collection,
  query,
  orderBy,
  startAfter as startAfterDoc,
  limit as limitFn,
  where,
  getDocs,
} from "firebase/firestore";

import { db } from "../auth/firebase";

const COLLECTION = "enquiries";


// --------------------------------------------------
// Map enquiry data before saving to Firestore
// --------------------------------------------------
const mapToEnquiry = (data) => ({
  // Basic student details
  firstName: data.firstName || "",
  lastName: data.lastName || "",

  // Newly added student details
  studentId: data.studentId || "",
  dateOfBirth: data.dateOfBirth || "",
  address: data.address || "",
  usi: data.usi || "",
  courseEnrolledIn: data.courseEnrolledIn || "",

  // Contact details
  email: data.email || "",
  phone: data.phone || "",

  // Information request options
  informationRequests: Array.isArray(data.informationRequests)
    ? data.informationRequests
    : [],

  otherInformationRequest: data.otherInformationRequest || "",

  // Existing enquiry details
  question: data.question || "",

  courses: Array.isArray(data.courses)
    ? data.courses
    : [],

  agent: data.agent || "",
  customAgent: data.customAgent || "",

  status: data.status || "new",

  notes: data.notes || "",

  // Dates
  createdAt: data.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});


// --------------------------------------------------
// Map enquiry while updating status
// --------------------------------------------------
const mapToEnquiryWithStatus = (data, status) => ({
  ...mapToEnquiry(data),
  status,
});


// --------------------------------------------------
// Create new enquiry
// --------------------------------------------------
export const createEnquiry = async (data, id) =>
  await createDocument(
    COLLECTION,
    mapToEnquiry(data),
    id
  );


// --------------------------------------------------
// Update existing enquiry
// --------------------------------------------------
export const updateEnquiry = async (id, data) =>
  await updateDocument(
    COLLECTION,
    id,
    mapToEnquiry(data)
  );


// --------------------------------------------------
// Update enquiry status
// --------------------------------------------------
export const updateStatusEnquiry = async (
  id,
  data,
  updateStatus
) => {
  return await updateDocument(
    COLLECTION,
    id,
    mapToEnquiryWithStatus(data, updateStatus)
  );
};


// --------------------------------------------------
// Delete enquiry
// --------------------------------------------------
export const deleteEnquiry = async (id) =>
  await deleteDocument(
    COLLECTION,
    id
  );


// --------------------------------------------------
// Get single enquiry
// --------------------------------------------------
export const getEnquiry = async (id) =>
  await getDocument(
    COLLECTION,
    id
  );


// --------------------------------------------------
// Get all enquiries
// --------------------------------------------------
export const getAllEnquiries = async () =>
  await getCollection(COLLECTION);


// --------------------------------------------------
// Get enquiries with pagination/search
// --------------------------------------------------
export const getEnquiriesPage = async ({
  limit = 10,
  startAfter = null,
  searchText = "",
} = {}) => {

  const fieldsToSearch = [
    "firstName",
    "lastName",
    "agent",
  ];

  const resultsMap = new Map();

  // Run Firestore query for a specific field
  const runQuery = async (field) => {

    let q = query(
      collection(db, COLLECTION),
      orderBy("createdAt"),
      limitFn(limit)
    );

    if (startAfter) {
      q = query(
        q,
        startAfterDoc(startAfter)
      );
    }

    if (searchText) {
      q = query(
        q,
        where(field, "==", searchText)
      );
    }

    const snapshot = await getDocs(q);

    snapshot.forEach((doc) => {
      resultsMap.set(
        doc.id,
        {
          id: doc.id,
          ...doc.data(),
        }
      );
    });
  };


  // Search multiple fields
  if (searchText) {

    await Promise.all(
      fieldsToSearch.map((field) =>
        runQuery(field)
      )
    );

  } else {

    // Normal pagination
    await runQuery(fieldsToSearch[0]);

  }


  const data = Array.from(
    resultsMap.values()
  );

  return data;
};


// --------------------------------------------------
// Get count of new enquiries
// --------------------------------------------------
export const getNewEnquiryCount = async () => {

  const q = query(
    collection(db, COLLECTION),
    where("status", "==", "new")
  );

  const snapshot = await getDocs(q);

  return snapshot.size;
};